import { Component, NgZone, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { UserHttpService } from '@services/datasources/user-http.service';
import { FeatureService } from '@services/features/feature.service';
import { OrganizationAction } from '@state/organization.state';
import { UserAction, UserState } from '@state/user.state';
import { UserClaims } from 'app/@types/dto/user-claims.model';
import { UserTask } from 'app/@types/dto/user-task.model';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { User } from 'app/auth/models';
import { catchError, combineLatest, forkJoin, map, Observable, of, Subject, tap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { LoaderService } from '@services/loader/loader.service';
import { QuestionnaireHttpService } from '@services/datasources/questionnaire-http.service';
import { QuestionnaireAction } from '@state/questionnaire.state';
import { SolutionAction } from '@state/solution.state';
import countryData from '@assets/data/countries.json';
import { RoleHttpService } from '@services/datasources/role-http.service';
import { AdminHTTPService } from '@services/datasources/admin-http-service';
import { QuestionnaireConfigAction } from '@state/questionnaire.config.state';
import { Country } from 'app/@types/shared/world-cities';

type DisplayOrg = OrganizationDto & {
    profile: string;
    isLicenseValid: boolean;
    validUntil: Date;
};
@Component({
    selector: 'app-change-organization-modal',
    templateUrl: './change-organization-modal.component.html',
    styleUrls: ['./change-organization-modal.component.scss'],
    standalone: false,
})
export class ChangeOrganizationModalComponent implements OnInit {
    @Select(UserState.getCurrentUser) user$!: Observable<User>;
    @Select(UserState.getCurrentUserClaims) userClaims$!: Observable<UserClaims>;
    allOrganizations: DisplayOrg[] = [];
    organizations: DisplayOrg[] = [];
    fallBackLogo = '';

    user: User | null = null;

    userClaims: UserClaims | null = null;

    constructor(
        private readonly userService: UserHttpService,
        private readonly store: Store,
        private readonly _router: Router,
        private readonly loaderService: LoaderService,
        private readonly featureService: FeatureService,
        private readonly questionnaireService: QuestionnaireHttpService,
        private readonly roleService: RoleHttpService,
        private adminHttpService: AdminHTTPService,
        private ngZone: NgZone,
    ) {}

    ngOnInit(): void {
        combineLatest([this.userService.getAvailableOrganization(), this.userClaims$, this.user$]).subscribe(
            ([organizations, userClaim, user]) => {
                this.organizations = new Array<DisplayOrg>();
                const organizationObservables = organizations.map((org) =>
                    this.adminHttpService.getOrganizationPricingPlan(org.uid ?? '').pipe(
                        map((pricingPlan) => ({
                            ...org,
                            profile: userClaim.tasks.find(
                                (task) => task.name === 'orguser' && task.organization_id === org.uid,
                            )
                                ? 'Owner'
                                : 'User',
                            isLicenseValid: new Date(pricingPlan?.validUntil).getTime() > new Date().getTime(),
                            validUntil: new Date(pricingPlan?.validUntil),
                        })),
                    ),
                );

                forkJoin(organizationObservables).subscribe((orgs) => {
                    this.organizations = orgs;
                    this.allOrganizations = [...this.organizations];
                });
                this.userClaims = userClaim;
                this.user = user;
            },
        );
        this.fallBackLogo = `assets/images/logo/logo-h.webp`;
    }

    onSearch(searchValue: string | null): void {
        if (searchValue === null || searchValue === '') {
            this.organizations = this.allOrganizations;
        } else {
            this.organizations = this.allOrganizations.filter((org) =>
                org.name.toUpperCase().includes(searchValue.toUpperCase()),
            );
        }
    }

    onClickOrg(org: DisplayOrg): void {
        if (!org || !org.uid || !this.userClaims || !org.isLicenseValid) {
            return;
        }
        const orgId = org.uid;

        this.loaderService.setLoading(true);

        forkJoin({
            activateOrg: this.activateOrganization(orgId),
            questionnaires: this.fetchAndActivateQuestionnaire(orgId),
            orgClaims: this.fetchOrgScopedClaims(orgId),
        }).subscribe({
            next: ({ orgClaims }) => {
                const tasks = this.mergeOrgScopedTasks(orgId, orgClaims);
                this.store.dispatch(
                    new UserAction.UpdateWithValue({
                        ...this.userClaims,
                        can_sudo: false,
                        is_sudo: false,
                        organization: org,
                        userRole: orgClaims?.userRole,
                        // @ts-expect-error: TODO: Remove ignore when types are fixed
                        entities_count: this.userClaims?.entities_count,
                        // @ts-expect-error: TODO: Remove ignore when types are fixed
                        max_entities: this.userClaims?.max_entities,
                        // @ts-expect-error: TODO: Remove ignore when types are fixed
                        user_email: this.userClaims?.user_email,
                        tasks,
                    }),
                );
                if (this.userClaims) {
                    this.featureService.handleUserClaims({
                        ...this.userClaims,
                        can_sudo: false,
                        is_sudo: false,
                        organization: org,
                        userRole: orgClaims?.userRole,
                        tasks,
                    });
                }
            },
            error: () => {
                this.loaderService.setLoading(false);
            },
        });
    }

    private activateOrganization(orgId: string): Observable<void> {
        this.store.dispatch(new OrganizationAction.Activate(orgId));
        return of(void 0); // Return an observable to integrate with forkJoin
    }

    /**
     * Refetch the claims scoped to the organization being entered. The claims held before
     * selection were fetched without an organization (multi-org users land on this page
     * precisely because no org could be auto-selected), so they carry no org-scoped fields —
     * in particular `userRole`, which drives the Viewer read-only UI.
     *
     * Only `userRole` and the tasks are merged into the patched claims (not the full response):
     * swapping in the whole org-scoped claims would also populate `organization_entities`, which
     * unblocks AppComponent's claims-driven auto-activation and would race the questionnaire
     * activation this page already performs itself.
     *
     * Non-blocking on failure: the user still enters the organization (pre-fix behaviour) and
     * the backend keeps enforcing write permissions — only the read-only styling is lost.
     */
    private fetchOrgScopedClaims(orgId: string): Observable<UserClaims | null> {
        return this.userService.getUserClaims(orgId).pipe(catchError(() => of(null)));
    }

    /**
     * Tasks for the claims patched on selection. The session claims may themselves be scoped to
     * one organization (a "control" session restored at sign-in): their tasks are then all bound
     * to that organization, and the route guards deny every other one. The selected organization's
     * tasks therefore come from its org-scoped claims; the tasks bound to other organizations are
     * kept, as this page's Owner/User column reads them.
     *
     * Falls back to the session tasks when the org-scoped claims carry none (refetch failed,
     * inactive organization, no authorized entity) — the pre-fix behaviour.
     */
    private mergeOrgScopedTasks(orgId: string, orgClaims: UserClaims | null): UserTask[] {
        const sessionTasks = this.userClaims?.tasks ?? [];
        if (!orgClaims?.tasks?.length) {
            return sessionTasks;
        }
        return [...sessionTasks.filter((task) => task.organization_id !== orgId), ...orgClaims.tasks];
    }

    private fetchAndActivateQuestionnaire(orgId: string): Observable<void> {
        return this.questionnaireService.getAll(orgId).pipe(
            map((instances) =>
                instances
                    .map(({ questionnaire }) => questionnaire)
                    .filter((q) => !q.deleted)
                    .sort((a, b) => new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime()),
            ),
            tap((sortedQuestionnaires) => {
                if (sortedQuestionnaires.length > 0) {
                    const latest = sortedQuestionnaires[0];
                    const activationAwaiter = new Subject<boolean>();
                    this.store.dispatch(
                        new QuestionnaireAction.Activate(
                            orgId,
                            latest.refYear,
                            latest.organizationEntityId,
                            false,
                            activationAwaiter,
                        ),
                    );
                    activationAwaiter.subscribe((isActivated) => {
                        if (isActivated) {
                            this.loaderService.setLoading(false);
                            this.ngZone.run(() => {
                                this._router.navigate(['/app/measure-summary']);
                            });
                        }
                    });
                    this.store.dispatch(new QuestionnaireConfigAction.Activate(orgId, latest.organizationEntityId));
                    if (latest.id && latest.typeId === 1) {
                        this.store.dispatch(new SolutionAction.Activate(orgId, latest.id));
                    }
                }
            }),
            catchError((err) => {
                console.error('Error fetching or processing questionnaires:', err);
                return throwError(err);
            }),
            map(() => void 0), // Return an observable to integrate with forkJoin
        );
    }

    findLocationLabel(code: string) {
        return countryData.find((cntry: Country) => cntry.id === code)?.name;
    }

    getOrgValidity(org: DisplayOrg) {
        const formatter = new Intl.DateTimeFormat('fr-FR');

        return `${formatter.format(new Date(org.validUntil))}`;
    }
}
